import '../icon/ui-icon';
import { UIElement } from '../ui-element';
import { Labels } from '../../global/labels';
import { focusHandler } from '../../global/focus-handler';
import { getKeyByValue, scrollTo } from '../../global/helpers';
import {
    createElement,
    mutateTo,
    updateElement,
} from '../../global/ui-helpers';
import { Component } from '../../decorators/component.decorator';
import { Attribute } from '../../decorators/attribute.decorator';
import { Child } from '../../decorators/child.decorator';
import { UIIcon } from '../icon/ui-icon';
import type { TranslationLabels } from '../../types/labels.type';
import { ComponentState } from '../ui-element.const';
import {
    UIAnchorDirections,
    UIAnchorDirection,
    UIAnchorPlacement,
} from './ui-anchor.helpers';
import styles from './ui-anchor.css';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UIAnchor
 * @element ui-anchor
 * @classdesc Represents a class for <code>ui-anchor</code> element.
 * Scrolls to element.
 * @fires event:scroll
 * @fires event:visibility
 * @property {UIAnchorPlacement} [placement="bottom-right"] {@attr placement} Position for arrow.
 *   {@desc top-left}
 *   {@desc top-middle}
 *   {@desc top-right}
 *   {@desc bottom-left}
 *   {@desc bottom-middle}
 *   {@desc bottom-right}
 * @property {string} [for] {@attr for} Scrolls to element id or top if not supplied.
 * @property {string} [direction="top"] {@attr direction} Glyph icon direction
 *  {@desc top}
 *  {@desc bottom}
 *  {@desc right}
 *  {@desc left}
 * @property {HTMLButtonElement} button Shortcut to anchor button.
 * @property {HTMLButtonElement} icon Shortcut to ui-icon.
 * @example
 * <ui-anchor placement="bottom-right"></ui-anchor>
 */
@Component({
    tag: 'ui-anchor',
    styles,
})
export class UIAnchor extends UIElement {
    @Attribute(String) placement: UIAnchorPlacement = 'bottom-right';
    @Attribute(String) direction: UIAnchorDirection = 'top';
    @Attribute(String) for?: string;
    @Child('button') button!: HTMLButtonElement;
    @Child('ui-icon') icon!: UIIcon;

    private intersectionObserver!: IntersectionObserver;

    /**
     * @type {Array<string>}
     */
    static get observedAttributes(): Array<string> {
        return ['direction'];
    }

    /**
     * @type {Labels}
     * @readonly
     */
    static get labels(): TranslationLabels.Labels {
        return Labels.attach('ui-anchor', {
            top: 'Scroll to the top of the page',
            bottom: 'Scroll to the bottom of the page',
            left: 'Scroll to the left',
            right: 'Scroll to the right',
        });
    }

    /**
     * Glyph direction mapping.
     * @deprecated This property for backwards compatibility. Use UIAnchorDirections constant instead.
     * @type {UIAnchorDirections}
     * @readonly
     */
    static readonly glyphDirection = UIAnchorDirections;

    /**
     * Gets the position of an element relative to the document
     * @param {HTMLElement} element
     * @returns {{left: number, top: number}}
     */
    static getElementOffset(element: HTMLElement): Partial<DOMRect> {
        const rect = element.getBoundingClientRect();
        return {
            left: rect.left + window.scrollX,
            top: rect.top + window.scrollY,
        };
    }

    /**
     * Gets scroll target
     * @param {string} [elementId]
     * @returns {HTMLElement | number}
     */
    static getScrollTarget(elementId?: string): HTMLElement | number {
        let element;
        if (elementId) {
            element = document.getElementById(elementId);
        }
        return element ?? 0;
    }

    /**
     * Handles click event for button.
     * @private
     */
    private handleClickEvent = (): void => {
        void this.scrollToElement(this.for);
    };

    /**
     * Handles intersection observer entries.
     * @param {Array<IntersectionObserverEntry>} entries
     * @private
     */
    private handleIntersectionEntries(
        entries: Array<IntersectionObserverEntry>
    ): void {
        entries.forEach((entry) => {
            this.toggleVisibility(entry.isIntersecting);
        });
    }

    /**
     * Scrolls to element.
     * @param {string} [elementId]
     * @returns {Promise<void>}
     */
    async scrollToElement(elementId?: string): Promise<void> {
        this.dispatchCustomEvent('scroll', { completed: false });
        const target = UIAnchor.getScrollTarget(elementId);
        await scrollTo(target);
        focusHandler.focusScrollTarget(target);
        this.dispatchCustomEvent('scroll', { completed: true });
    }

    /**
     * Toggles component visibility.
     * @param {boolean} [visible]
     */
    toggleVisibility(visible: boolean = false): void {
        const intersecting = '-intersecting';
        const shouldToggle = this.classList.contains(intersecting) !== visible;
        if (shouldToggle) {
            visible
                ? this.classList.add(intersecting)
                : this.classList.remove(intersecting);
            this.dispatchCustomEvent('visibility', { visible: visible });
        }
    }

    /**
     * Updates icon
     */
    updateIcon() {
        const element = this.renderIcon();
        if (element.glyph !== this.icon.glyph) {
            mutateTo(this.icon, element, true);
        }
    }

    /**
     * Renders button
     * @param {UIIcon} icon
     * @returns {HTMLButtonElement}
     */
    renderButton(icon: UIIcon): HTMLButtonElement {
        const glyph =
            UIAnchor.glyphDirection[this.direction] ||
            UIAnchor.glyphDirection.top;
        const labelDirection = getKeyByValue(UIAnchor.glyphDirection, glyph);
        return createElement<HTMLButtonElement>({
            tagName: 'button',
            attributes: {
                'aria-label': labelDirection
                    ? UIAnchor.labels[labelDirection]
                    : null,
            },
            classList: {
                'button': true,
                '-guiding': true,
            },
            children: [icon],
        })!;
    }

    /**
     * Renders icon
     * @returns {UIIcon}
     */
    renderIcon(): UIIcon {
        const glyph =
            UIAnchor.glyphDirection[this.direction] ||
            UIAnchor.glyphDirection.top;
        return createElement<UIIcon>({
            tagName: 'ui-icon',
            attributes: { glyph },
            classList: {
                '-navicon': true,
                'ui-anchor__icon': true,
            },
        })!;
    }

    /**
     * Sets component top offset
     * @returns {UIAnchor}
     */
    setTopOffset(): UIAnchor {
        if (this.for) {
            const target = UIAnchor.getScrollTarget(this.for);
            if (target instanceof HTMLElement) {
                const offset = UIAnchor.getElementOffset(target);
                this.style.top = `${offset.top}px`;
            }
        }
        return this;
    }

    /**
     * @inheritDoc
     */
    observeAttributes(
        name: string,
        oldValue: string | null,
        newValue: string | null
    ): void {
        if (this.state !== ComponentState.Hydrated) {
            return;
        }

        switch (name) {
            case 'direction':
                this.updateIcon();
                break;
            /* istanbul ignore next */
            default:
                break;
        }
    }

    /**
     * Subscribes to events
     * @private
     * @returns {UIAnchor}
     */
    subscribeEvents(): UIAnchor {
        updateElement(this.button, {
            events: {
                click: this.handleClickEvent,
            },
        });
        this.intersectionObserver = new IntersectionObserver(
            this.handleIntersectionEntries.bind(this)
        );
        this.intersectionObserver.observe(this);
        return this;
    }

    /**
     * Unsubscribes from events.
     * @private
     */
    private unsubscribeEvents(): void {
        this.button.removeEventListener('click', this.handleClickEvent);
    }

    /**
     * @inheritDoc
     */
    render(): void {
        const icon = this.renderIcon();
        const button = this.renderButton(icon);
        this.insertElements([button]);
        this.updateElement({
            classList: { '-intersecting': true },
            attributes: {
                placement: this.placement,
                direction: this.direction,
            },
        });
    }

    /**
     * @inheritDoc
     */
    hydrate(): void {
        this.setTopOffset();
        this.subscribeEvents();
    }

    /**
     * @inheritDoc
     */
    disconnect(): void {
        this.intersectionObserver?.disconnect();
        this.unsubscribeEvents();
    }

    /**
     * @inheritDoc
     */
    reconnect(): void {
        this.intersectionObserver?.observe(this);
        this.subscribeEvents();
    }
}
